import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, firstValueFrom, from, map, switchMap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { supabase } from '../supabase.client';

export interface User {
  id: string;
  email: string;
  name: string;
  nickname?: string;
  avatarUrl?: string;
  isAdmin: boolean;
  supabaseAuthId: string;
  telemetryClientDownloadClicked: boolean;
  onboardingCompleted: boolean;
  firstValidLapSubmittedAt?: string;
  firstValidLapWithin24h?: boolean;
  iracingUserId?: number;
  irating?: number;
  iracingLicenseClass?: string;
  iracingSafetyRating?: number;
  iracingProfile?: any;
  iracingProfileUpdatedAt?: string;
}

export interface AuthResponse {
  user: User;
  token: string;
  refreshToken?: string;
}

export interface RegisterResponse {
  requiresEmailConfirmation: boolean;
  message: string;
}

export interface TelemetryAuthCodeResponse {
  code: string;
  expiresInSeconds: number;
}

export interface DevMockUser {
  key: string;
  email: string;
  name: string;
  iRating: number;
  licenseClass: string;
  safetyRating: number;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiUrl = `${environment.apiUrl}/auth`;
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  private tokenKey = 'auth_token';
  private userKey = 'auth_user';
  private initPromise: Promise<void>;

  constructor(private http: HttpClient, private router: Router) {
    this.initPromise = this.initializeAuth().catch((error) => {
      console.error('Failed to initialize authentication state:', error);
      this.clearLocalAuth();
    });
  }

  private async initializeAuth(): Promise<void> {
    const { data } = await supabase.auth.getSession();
    const fallbackToken = this.getToken();
    await this.applySession(data.session?.access_token ?? fallbackToken ?? null);

    supabase.auth.onAuthStateChange((_event: unknown, session: { access_token: string } | null) => {
      const fallbackToken = this.getToken();
      void this.applySession(session?.access_token ?? fallbackToken ?? null);
    });
  }

  private async applySession(accessToken: string | null): Promise<void> {
    if (!accessToken) {
      this.clearLocalAuth();
      return;
    }

    localStorage.setItem(this.tokenKey, accessToken);

    try {
      const response = await this.fetchCurrentUser(accessToken);
      this.setCurrentUser(response.user);
    } catch (error) {
      this.clearLocalAuth();
      await supabase.auth.signOut();
    }
  }

  private fetchCurrentUser(token: string): Promise<{ user: User }> {
    return firstValueFrom(
      this.http.get<{ user: User }>(`${this.apiUrl}/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    );
  }

  private setCurrentUser(user: User): void {
    localStorage.setItem(this.userKey, JSON.stringify(user));
    this.currentUserSubject.next(user);
  }

  private clearLocalAuth() {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userKey);
    this.currentUserSubject.next(null);
  }

  ready(): Promise<void> {
    return this.initPromise;
  }

  register(data: { nickname: string; email: string; password: string }): Observable<RegisterResponse> {
    const redirectTo = `${window.location.origin}/auth/callback`;
    return this.http
      .post<RegisterResponse>(`${this.apiUrl}/register`, { ...data, emailRedirectTo: redirectTo });
  }

  login(data: { email: string; password: string }): Observable<void> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/login`, data).pipe(
      switchMap((response) => from(this.applyAuthResponse(response))),
      map(() => void 0)
    );
  }

  loginWithGoogle(returnUrl: string = '/sessions'): Observable<void> {
    const redirectTo = `${window.location.origin}/auth/callback?returnUrl=${encodeURIComponent(returnUrl)}`;

    return from(
      supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo },
      })
    ).pipe(
      switchMap(({ error }) => {
        if (error) {
          return throwError(() => error);
        }
        return from(Promise.resolve());
      })
    );
  }

  createTelemetryClientAuthCode(): Observable<TelemetryAuthCodeResponse> {
    return from(supabase.auth.getSession()).pipe(
      switchMap(({ data }) =>
        this.http.post<TelemetryAuthCodeResponse>(`${this.apiUrl}/telemetry-client/code`, {
          refreshToken: data.session?.refresh_token || undefined,
        })
      )
    );
  }

  getDevMockUsers(): Observable<DevMockUser[]> {
    return this.http
      .get<{ users: DevMockUser[] }>(`${this.apiUrl}/dev/mock-users`)
      .pipe(map((response) => response.users || []));
  }

  loginAsDevMockUser(key: string): Observable<void> {
    return this.http
      .post<AuthResponse>(`${this.apiUrl}/dev/mock-login`, { key })
      .pipe(
        switchMap((response) => from(this.applyAuthResponse(response))),
        map(() => void 0)
      );
  }

  private async applyAuthResponse(response: AuthResponse): Promise<void> {
    if (response.refreshToken) {
      try {
        await supabase.auth.setSession({
          access_token: response.token,
          refresh_token: response.refreshToken,
        });
      } catch (error) {
        console.warn('Failed to persist Supabase session, falling back to access token only:', error);
      }
    }

    await this.applySession(response.token);
  }

  markTelemetryClientDownloadClicked(): Observable<User> {
    return this.http
      .post<{ user: User }>(`${this.apiUrl}/telemetry-client-download/click`, {})
      .pipe(
        map((response) => response.user),
        switchMap((user) => {
          this.setCurrentUser(user);
          return from(Promise.resolve(user));
        })
      );
  }

  markOnboardingCompleted(nickname?: string | null): Observable<User> {
    const payload: { nickname?: string | null } = {};
    if (nickname !== undefined) {
      payload.nickname = nickname;
    }

    return this.http
      .post<{ user: User }>(`${this.apiUrl}/onboarding/complete`, payload)
      .pipe(
        map((response) => response.user),
        switchMap((user) => {
          this.setCurrentUser(user);
          return from(Promise.resolve(user));
        })
      );
  }

  updateProfile(payload: { avatarUrl?: string | null; nickname?: string | null }): Observable<User> {
    return this.http
      .put<{ user: User }>(`${this.apiUrl}/profile`, payload)
      .pipe(
        map((response) => response.user),
        switchMap((user) => {
          this.setCurrentUser(user);
          return from(Promise.resolve(user));
        }),
      );
  }

  async handleAuthCallback(): Promise<void> {
    const currentUrl = window.location.href;
    const url = new URL(currentUrl);
    const authCode = url.searchParams.get('code');
    const authError = url.searchParams.get('error');
    const authErrorDescription = url.searchParams.get('error_description');

    if (authError) {
      throw new Error(authErrorDescription || authError);
    }

    // If Supabase already processed the callback URL, reuse existing session.
    const existingSession = await supabase.auth.getSession();
    if (existingSession.data.session?.access_token) {
      await this.applySession(existingSession.data.session.access_token);
      return;
    }

    if (authCode) {
      const { error } = await supabase.auth.exchangeCodeForSession(currentUrl);
      if (error) {
        throw error;
      }
    }

    // Session propagation can lag slightly after redirect.
    for (let i = 0; i < 5; i += 1) {
      const { data } = await supabase.auth.getSession();
      if (data.session?.access_token) {
        await this.applySession(data.session.access_token);
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 150));
    }

    throw new Error('Authentication callback failed');
  }

  logout() {
    void supabase.auth.signOut();
    this.clearLocalAuth();
    this.router.navigate(['/login']);
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  isAuthenticated(): boolean {
    return !!this.currentUserSubject.value && !!this.getToken();
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

}
